// Tally-parity voucher-type switching: when the user presses F4–F9 (or clicks
// the type strip) INSIDE a voucher entry form, the draft is not discarded —
// what can carry, carries. In Tally, switching Sales → Payment keeps the
// selected party, the date and the narration; item lines carry between
// invoice-type vouchers; journal lines carry between double-entry vouchers.
//
// Mechanics: the active form registers a collector; the global F-key handler
// (App.tsx) or the VoucherTypeSwitcher strip collects the draft, stashes it
// here, navigates, and the target form hydrates from it on mount.
//
// The stash self-expires after a few seconds instead of being cleared on
// read — React StrictMode double-mounts would otherwise apply-then-lose it —
// so a form opened normally minutes later never sees a stale draft.

export interface ItemCarryLine {
  itemId: string;
  itemName: string;
  hsnSacCode: string;
  unitCode: string;
  qty: string;
  qtyPcs: string;
  rate: string;
  discountPct: string;
  gstPct: string;
}

export interface JournalCarryLine {
  ledgerId: string;
  ledgerName: string;
  signedAmount: string;
  type: 'DR' | 'CR';
  description: string;
}

export interface VoucherCarry {
  date: string;
  narration: string;
  partyLedgerId: string | null;
  partyLedgerName: string | null;
  itemLines?: ItemCarryLine[];
  journalLines?: JournalCarryLine[];
}

const CARRY_TTL_MS = 5_000;

let stash: { carry: VoucherCarry; at: number } | null = null;

export function setVoucherCarry(carry: VoucherCarry) {
  stash = { carry, at: Date.now() };
}

/** Read the carried draft if one was stashed within the last few seconds. */
export function takeVoucherCarry(): VoucherCarry | null {
  if (!stash) return null;
  if (Date.now() - stash.at > CARRY_TTL_MS) { stash = null; return null; }
  return stash.carry;
}

// ── Collector registry ─────────────────────────────────────────────
// The mounted voucher form registers a function that snapshots its draft.
// Read inside event handlers only — deliberately not React state.

let collector: (() => VoucherCarry) | null = null;

export function registerCarryCollector(fn: () => VoucherCarry): () => void {
  collector = fn;
  return () => { if (collector === fn) collector = null; };
}

export function collectCarry(): VoucherCarry | null {
  return collector ? collector() : null;
}
